import {onMounted, onBeforeUnmount} from 'vue';
import {send} from '@/composables/apiClient';

// Crash and SOS stay unread until the user opens the alert itself or uses Mark all read.
const KEPT_ALERT_TYPES = [8, 13];
export const isKeptFromAutoRead = (row) => KEPT_ALERT_TYPES.includes(Number(row?.alert_type));

// Opening an alerts screen reads it. Call `attempt()` after every successful
// list load; it POSTs notifications/read at most once at a time, only while the
// browser tab is visible, only when `hasUnread()` says the loaded rows include
// something the call will actually mark, and never for a viewer `enabled()`
// rules out (the API no-ops for admin roles, whose rows belong to others).
// `hasUnread()` must ignore kept rows: they stay unread after the call, and the
// retry below would otherwise post forever.
//
// `onMarked()` flips the rows locally BEFORE the request: a row control used
// during the round trip then sees a read row and sends no PUT toggle, which
// would otherwise flip the just-read row back to unread. The 201 response
// carries unreadNotifications, which the apiClient interceptor writes into
// the bell badge, so the caller does not touch the badge. A failed request is
// silent: the rows come back unread on the next load and this runs again.
export function useAutoMarkRead({enabled, hasUnread, onMarked}) {
    let inFlight = false;
    const attempt = () => {
        if (inFlight || !enabled() || document.visibilityState !== 'visible' || !hasUnread()) return;
        inFlight = true;
        onMarked();
        send('post', 'notifications/read', {keep_urgent: 1})
            .catch(() => {})
            .finally(() => {
                inFlight = false;
                // Rows that loaded during the round trip (load more, a filter,
                // a refresh) were skipped above; give them their turn.
                attempt();
            });
    };
    // A load that finished while the tab was hidden was never shown; mark it
    // when the tab comes back.
    const onVisibility = () => {
        if (document.visibilityState === 'visible') attempt();
    };
    onMounted(() => document.addEventListener('visibilitychange', onVisibility));
    onBeforeUnmount(() => document.removeEventListener('visibilitychange', onVisibility));
    return {attempt};
}
